import { useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { track } from '../../lib/track'

export const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, delay, ease: 'easeOut' as const },
})

export interface AccentTheme {
  bg: string
  border: string
  text: string
  solid: string
  rgba8: string
  rgba30: string
  glow: string
  glowStrong: string
}

// Cores alinhadas com clients/matheus-edson/brand.json (visual_pattern.post_categories)
export const ACCENTS: Record<'blue' | 'amber' | 'red', AccentTheme> = {
  blue:  { bg: 'rgba(79,126,247,0.08)',  border: 'rgba(79,126,247,0.3)',  text: '#4f7ef7', solid: '#4f7ef7', rgba8: 'rgba(79,126,247,0.08)',  rgba30: 'rgba(79,126,247,0.3)',  glow: 'rgba(79,126,247,0.25)',  glowStrong: 'rgba(79,126,247,0.45)' },
  amber: { bg: 'rgba(251,191,36,0.08)',  border: 'rgba(251,191,36,0.3)',  text: '#fbbf24', solid: '#fbbf24', rgba8: 'rgba(251,191,36,0.08)',  rgba30: 'rgba(251,191,36,0.3)',  glow: 'rgba(251,191,36,0.25)',  glowStrong: 'rgba(251,191,36,0.45)' },
  red:   { bg: 'rgba(239,68,68,0.08)',   border: 'rgba(239,68,68,0.3)',   text: '#ef4444', solid: '#ef4444', rgba8: 'rgba(239,68,68,0.08)',   rgba30: 'rgba(239,68,68,0.3)',   glow: 'rgba(239,68,68,0.25)',   glowStrong: 'rgba(239,68,68,0.45)' },
}

export const MONO = "'JetBrains Mono', 'Fira Code', Consolas, Monaco, monospace"
export const SANS = "'Inter', system-ui, -apple-system, sans-serif"

// figlet font: Bloody. Palavras canônicas do brand (CANONICAL_WORDS).
// Cada palavra é a mesma usada no glitch title do post LinkedIn correspondente.
export const ASCII = {
  OCULTO: ` ▒█████   ▄████▄   █    ██  ██▓  ▄▄▄█████▓ ▒█████
▒██▒  ██▒▒██▀ ▀█   ██  ▓██▒▓██▒  ▓  ██▒ ▓▒▒██▒  ██▒
▒██░  ██▒▒▓█    ▄ ▓██  ▒██░▒██░  ▒ ▓██░ ▒░▒██░  ██▒
▒██   ██░▒▓▓▄ ▄██▒▓▓█  ░██░▒██░  ░ ▓██▓ ░ ▒██   ██░
░ ████▓▒░▒ ▓███▀ ░▒▒█████▓ ░██████▒▒██▒ ░ ░ ████▓▒░
░ ▒░▒░▒░ ░ ░▒ ▒  ░░▒▓▒ ▒ ▒ ░ ▒░▓  ░▒ ░░   ░ ▒░▒░▒░
  ░ ▒ ▒░   ░  ▒   ░░▒░ ░ ░ ░ ░ ▒  ░  ░      ░ ▒ ▒░
░ ░ ░ ▒  ░         ░░░ ░ ░   ░ ░   ░      ░ ░ ░ ▒
    ░ ░  ░ ░         ░         ░  ░           ░ ░
         ░                                        `,
  LENTO: ` ██▓    ▓█████  ███▄    █ ▄▄▄█████▓ ▒█████
▓██▒    ▓█   ▀  ██ ▀█   █ ▓  ██▒ ▓▒▒██▒  ██▒
▒██░    ▒███   ▓██  ▀█ ██▒▒ ▓██░ ▒░▒██░  ██▒
▒██░    ▒▓█  ▄ ▓██▒  ▐▌██▒░ ▓██▓ ░ ▒██   ██░
░██████▒░▒████▒▒██░   ▓██░  ▒██▒ ░ ░ ████▓▒░
░ ▒░▓  ░░░ ▒░ ░░ ▒░   ▒ ▒   ▒ ░░   ░ ▒░▒░▒░
░ ░ ▒  ░ ░ ░  ░░ ░░   ░ ▒░    ░      ░ ▒ ▒░
  ░ ░      ░      ░   ░ ░   ░      ░ ░ ░ ▒
    ░  ░   ░  ░         ░              ░ ░ `,
  HARD: ` ██░ ██  ▄▄▄       ██▀███  ▓█████▄
▓██░ ██▒▒████▄    ▓██ ▒ ██▒▒██▀ ██▌
▒██▀▀██░▒██  ▀█▄  ▓██ ░▄█ ▒░██   █▌
░▓█ ░██ ░██▄▄▄▄██ ▒██▀▀█▄  ░▓█▄   ▌
░▓█▒░██▓ ▓█   ▓██▒░██▓ ▒██▒░▒████▓
 ▒ ░░▒░▒ ▒▒   ▓▒█░░ ▒▓ ░▒▓░ ▒▒▓  ▒
 ▒ ░▒░ ░  ▒   ▒▒ ░  ░▒ ░ ▒░ ░ ▒  ▒
 ░  ░░ ░  ░   ▒     ░░   ░  ░ ░  ░
 ░  ░  ░      ░  ░   ░        ░
                            ░     `,
  SCHEMA: `  ██████  ▄████▄   ██░ ██ ▓█████  ███▄ ▄███▓ ▄▄▄
▒██    ▒ ▒██▀ ▀█  ▓██░ ██▒▓█   ▀ ▓██▒▀█▀ ██▒▒████▄
░ ▓██▄   ▒▓█    ▄ ▒██▀▀██░▒███   ▓██    ▓██░▒██  ▀█▄
  ▒   ██▒▒▓▓▄ ▄██▒░▓█ ░██ ▒▓█  ▄ ▒██    ▒██ ░██▄▄▄▄██
▒██████▒▒▒ ▓███▀ ░░▓█▒░██▓░▒████▒▒██▒   ░██▒ ▓█   ▓██▒
▒ ▒▓▒ ▒ ░░ ░▒ ▒  ░ ▒ ░░▒░▒░░ ▒░ ░░ ▒░   ░  ░ ▒▒   ▓▒█░
░ ░▒  ░ ░  ░  ▒    ▒ ░▒░ ░ ░ ░  ░░  ░      ░  ▒   ▒▒ ░
░  ░  ░  ░         ░  ░░ ░   ░   ░      ░     ░   ▒
      ░  ░ ░       ░  ░  ░   ░  ░       ░         ░  ░`,
  PARASITA: ` ██▓███   ▄▄▄       ██▀███   ▄▄▄        ██████  ██▓▄▄▄█████▓ ▄▄▄
▓██░  ██▒▒████▄    ▓██ ▒ ██▒▒████▄    ▒██    ▒ ▓██▒▓  ██▒ ▓▒▒████▄
▓██░ ██▓▒▒██  ▀█▄  ▓██ ░▄█ ▒▒██  ▀█▄  ░ ▓██▄   ▒██▒▒ ▓██░ ▒░▒██  ▀█▄
▒██▄█▓▒ ▒░██▄▄▄▄██ ▒██▀▀█▄  ░██▄▄▄▄██   ▒   ██▒░██░░ ▓██▓ ░ ░██▄▄▄▄██
▒██▒ ░  ░ ▓█   ▓██▒░██▓ ▒██▒ ▓█   ▓██▒▒██████▒▒░██░  ▒██▒ ░  ▓█   ▓██▒
▒▓▒░ ░  ░ ▒▒   ▓▒█░░ ▒▓ ░▒▓░ ▒▒   ▓▒█░▒ ▒▓▒ ▒ ░░▓    ▒ ░░    ▒▒   ▓▒█░
░▒ ░       ▒   ▒▒ ░  ░▒ ░ ▒░  ▒   ▒▒ ░░ ░▒  ░ ░ ▒ ░    ░      ▒   ▒▒ ░
░░         ░   ▒     ░░   ░   ░   ▒   ░  ░  ░   ▒ ░  ░        ░   ▒
               ░  ░   ░           ░  ░      ░   ░                 ░  ░`,
} as const

export type BloodyWord = keyof typeof ASCII

export function BloodyTitle({ accent, word }: { accent: AccentTheme; word: BloodyWord }) {
  return (
    <motion.div {...fade(0.05)} style={{
      marginBottom: '1.75rem',
      overflowX: 'auto',
      paddingBottom: '0.5rem',
    }}>
      <pre style={{
        fontFamily: MONO,
        fontSize: 'clamp(0.5rem, 1.15vw, 0.85rem)',
        lineHeight: 1,
        color: accent.solid,
        whiteSpace: 'pre',
        letterSpacing: 0,
        textShadow: `0 0 24px ${accent.glowStrong}, 0 0 60px ${accent.glow}`,
        filter: 'contrast(1.15)',
        fontWeight: 700,
        margin: 0,
        display: 'inline-block',
      }}>{ASCII[word]}</pre>
    </motion.div>
  )
}

export function CategoryLabel({ accent, text }: { accent: AccentTheme; text: string }) {
  return (
    <motion.div {...fade(0)} style={{
      fontFamily: MONO,
      fontSize: '0.74rem',
      color: accent.solid,
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
      marginBottom: '1.5rem',
      fontWeight: 500,
    }}>
      <span style={{ color: '#666' }}>{'// '}</span>{text}
    </motion.div>
  )
}

export function StatusBlock({ accent, rows }: { accent: AccentTheme; rows: { k: string; v: React.ReactNode }[] }) {
  return (
    <motion.div {...fade(0.18)} style={{
      background: 'linear-gradient(180deg, #111, #0d0d0d)',
      border: '1px solid #2a2a2a',
      borderLeft: `3px solid ${accent.solid}`,
      borderRadius: 10,
      padding: '1.1rem 1.4rem',
      marginBottom: '2.5rem',
      fontFamily: MONO,
      fontSize: '0.84rem',
      lineHeight: 1.9,
    }}>
      {rows.map((row, i) => (
        <div key={i} style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'baseline' }}>
          <span style={{ color: '#666', minWidth: 76, flexShrink: 0 }}>{row.k}:</span>
          <span style={{ color: '#e5e5e5', wordBreak: 'break-word', flex: '1 1 200px' }}>{row.v}</span>
        </div>
      ))}
    </motion.div>
  )
}

export function SignatureFooter({ accent }: { accent: AccentTheme }) {
  return (
    <motion.div {...fade(0.3)} style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      flexWrap: 'wrap',
      gap: '1rem',
      fontFamily: MONO,
      fontSize: '0.82rem',
      marginTop: '3.5rem',
      paddingTop: '1.5rem',
      borderTop: '1px solid #1e1e1e',
    }}>
      <div style={{ color: '#e5e5e5' }}>
        <span style={{ color: accent.solid, marginRight: 6 }}>→</span>
        <Link to="/" style={{ color: accent.solid, borderBottom: `1px solid ${accent.glow}`, paddingBottom: 1 }}>matheusedson.com</Link>
      </div>
      <div style={{ color: '#666', textAlign: 'right' }}>
        <span style={{ color: accent.solid }}>{'>_'}</span> matheus edson · GTM Engineer
      </div>
    </motion.div>
  )
}

export function DownloadButton({ content, filename, accent, source, mime = 'text/plain' }: { content: string; filename: string; accent: AccentTheme; source: string; mime?: string }) {
  const [done, setDone] = useState(false)
  const handle = () => {
    const blob = new Blob([content], { type: mime + ';charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    track('recurso_download', { resource: source, type: filename })
    setDone(true)
    setTimeout(() => setDone(false), 2000)
  }
  return (
    <button onClick={handle} style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.6rem 1.1rem',
      background: done ? accent.bg : accent.solid,
      color: done ? accent.text : '#0a0a0a',
      border: `1px solid ${done ? accent.border : 'transparent'}`,
      borderRadius: 7,
      fontFamily: MONO,
      fontSize: '0.82rem',
      fontWeight: 700,
      letterSpacing: '0.02em',
      cursor: 'pointer',
      transition: 'all 0.15s',
      textTransform: 'lowercase',
    }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
        <polyline points="7 10 12 15 17 10"/>
        <line x1="12" y1="15" x2="12" y2="3"/>
      </svg>
      {done ? 'baixado' : `baixar ${filename}`}
    </button>
  )
}

export function DownloadRow({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
      marginBottom: '1rem',
    }}>{children}</div>
  )
}

export function CodeBlock({ children, lang, accent, source }: { children: string; lang?: string; accent: AccentTheme; source: string }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    navigator.clipboard.writeText(children).then(() => {
      setCopied(true)
      track('recurso_copy', { resource: source, type: lang || 'snippet' })
      setTimeout(() => setCopied(false), 1500)
    })
  }
  return (
    <div style={{
      position: 'relative',
      background: '#070707',
      border: `1px solid ${accent.border}`,
      borderRadius: 8,
      overflow: 'hidden',
      marginBottom: '1rem',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.5rem 0.85rem',
        borderBottom: '1px solid #1e1e1e',
        background: 'rgba(255,255,255,0.02)',
      }}>
        <span style={{ fontFamily: MONO, fontSize: '0.68rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.12em' }}>{lang || 'text'}</span>
        <button
          onClick={handleCopy}
          style={{
            fontFamily: MONO,
            fontSize: '0.7rem',
            padding: '0.25rem 0.7rem',
            background: copied ? accent.bg : '#141414',
            color: copied ? accent.text : '#888',
            border: `1px solid ${copied ? accent.border : '#1e1e1e'}`,
            borderRadius: 5,
            cursor: 'pointer',
            transition: 'all 0.15s',
          }}
        >{copied ? '✓ copiado' : 'copiar'}</button>
      </div>
      <pre style={{
        margin: 0,
        padding: '1rem',
        overflowX: 'auto',
        fontSize: '0.78rem',
        lineHeight: 1.6,
        color: '#e5e5e5',
        fontFamily: MONO,
      }}>{children}</pre>
    </div>
  )
}

export function ResourceHero({ tag, title, desc, accent, source, word }: { tag: string; title: string; desc: string; accent: AccentTheme; source: string; word: BloodyWord }) {
  return (
    <>
      <BloodyTitle accent={accent} word={word} />
      <CategoryLabel accent={accent} text={tag} />
      <motion.div {...fade(0.1)} style={{ marginBottom: '1.75rem' }}>
        <h1 style={{
          fontFamily: SANS,
          fontSize: 'clamp(1.7rem, 4vw, 2.4rem)',
          fontWeight: 700,
          letterSpacing: '-0.025em',
          lineHeight: 1.15,
          marginBottom: '0.9rem',
          color: '#fff',
        }}>{title}</h1>
        <p style={{
          color: '#aaa',
          fontSize: '0.95rem',
          lineHeight: 1.65,
          maxWidth: 620,
        }}>{desc}</p>
        <p style={{ fontFamily: MONO, fontSize: '0.7rem', color: '#555', marginTop: '0.8rem' }}>
          {'// '}pode copiar, modificar e usar à vontade. sem captura de email. se ajudou,{' '}
          <a
            href={`mailto:linharesmatheus29@gmail.com?subject=Recurso ${source}&body=Oi Matheus, peguei o recurso ${source} e:`}
            onClick={() => track('recurso_email', { resource: source })}
            style={{ color: accent.solid, borderBottom: `1px solid ${accent.glow}` }}
          >manda um oi</a>.
        </p>
      </motion.div>
    </>
  )
}

export function ResourceContactCTA({ accent, source }: { accent: AccentTheme; source: string }) {
  return (
    <motion.section {...fade(0.2)} style={{ marginTop: '3rem' }}>
      <div style={{
        padding: '1.5rem 1.75rem',
        background: 'linear-gradient(180deg, #111, #0d0d0d)',
        border: `1px solid #2a2a2a`,
        borderLeft: `3px solid ${accent.solid}`,
        borderRadius: 10,
      }}>
        <p style={{ fontFamily: MONO, fontSize: '0.7rem', color: accent.solid, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 600, marginBottom: '0.6rem' }}>
          {'// '}quer ajuda implementando?
        </p>
        <p style={{ fontSize: '0.88rem', fontWeight: 500, marginBottom: '0.4rem', color: '#fff' }}>
          Mando um Loom de 5min vendo seu caso específico, ou marco uma sessão de 1h pra revisar com você. Sem cobrar nada.
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '1rem' }}>
          <a
            href={`mailto:linharesmatheus29@gmail.com?subject=Recurso ${source}&body=Oi Matheus, peguei o recurso e queria:`}
            onClick={() => track('cta_click', { location: `recurso_${source}`, type: 'email' })}
            style={{
              fontFamily: MONO,
              padding: '0.55rem 1.15rem',
              background: accent.solid,
              color: '#0a0a0a',
              borderRadius: 7,
              textDecoration: 'none',
              fontSize: '0.82rem',
              fontWeight: 700,
            }}
          >mandar mensagem</a>
        </div>
      </div>
    </motion.section>
  )
}

export function ResourceLayout({ children }: { children: React.ReactNode }) {
  return (
    <main style={{
      minHeight: '100vh',
      width: '100%',
      background: '#0a0a0a',
      color: '#e5e5e5',
      fontFamily: SANS,
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div aria-hidden="true" style={{
        position: 'absolute',
        inset: 0,
        background: 'repeating-linear-gradient(0deg, transparent 0, transparent 3px, rgba(255,255,255,0.012) 3px, rgba(255,255,255,0.012) 4px)',
        pointerEvents: 'none',
        zIndex: 1,
      }} />
      <div aria-hidden="true" style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: '60%',
        height: '500px',
        background: 'radial-gradient(circle at 80% 15%, rgba(79,126,247,0.10), transparent 60%)',
        pointerEvents: 'none',
        zIndex: 1,
      }} />

      <div style={{
        position: 'relative',
        zIndex: 2,
        maxWidth: 920,
        margin: '0 auto',
        padding: '1.1rem clamp(1rem, 4vw, 1.5rem)',
        fontFamily: MONO,
        fontSize: '0.72rem',
        color: '#666',
        borderBottom: '1px solid #1e1e1e',
        marginBottom: '0',
      }}>
        <Link to="/" style={{ color: '#888', textDecoration: 'none' }}>
          <span style={{ color: '#4f7ef7' }}>{'>_'}</span> matheusedson.com
        </Link>
      </div>

      <div style={{
        position: 'relative',
        zIndex: 2,
        maxWidth: 780,
        margin: '0 auto',
        padding: 'clamp(2rem, 6vw, 3rem) clamp(1rem, 4vw, 1.5rem) clamp(4rem, 10vw, 6rem)',
      }}>
        {children}
      </div>
    </main>
  )
}

export function H2({ children, accent }: { children: React.ReactNode; accent: AccentTheme }) {
  return (
    <h2 style={{
      fontFamily: MONO,
      fontSize: '0.74rem',
      color: accent.solid,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      marginBottom: '1rem',
      fontWeight: 700,
    }}>
      <span style={{ color: '#555' }}>{'// '}</span>{children}
    </h2>
  )
}
